import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Show } from "shared/components";
import { Banner } from "shared/components/Banner";
import { cx } from "shared/utils";
import { UloopPricing } from "types/landlord/partner";

interface PricingOption {
  value: UloopPricing;
  title: string;
  price: string;
  description: string;
}

const PRICING_OPTIONS: PricingOption[] = [
  {
    value: "lead",
    title: "Per-lead",
    price: "$5 per lead",
    description: "Pay Uloop only for the leads they send you."
  },
  {
    value: "featured",
    title: "Featured",
    price: "$250 per property per month",
    description: "Unlimited leads."
  }
];

interface UloopPricingModelProps {
  value: UloopPricing;
  onChange: (value: UloopPricing) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

export const UloopPricingModel = ({
  value,
  onChange,
  label = "Choose a pricing model",
  disabled = false,
  className
}: UloopPricingModelProps) => (
  <div className={cx("flex flex-col gap-3", className)}>
    <p className="text-xs font-bold uppercase tracking-wide text-content-400">
      {label}
    </p>

    <div role="radiogroup" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {PRICING_OPTIONS.map((option) => {
        const isSelected = value === option.value;

        return (
          <button
            key={option.title}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cx(
              "flex flex-col gap-2.5 rounded-xl border-2 p-4 text-left transition-colors",
              disabled && "cursor-not-allowed opacity-60",
              isSelected
                ? "border-primary-600 bg-primary-20"
                : "border-alpha-100 bg-background-1000 hover:border-alpha-200"
            )}
          >
            <span className="flex items-center gap-2.5">
              <span
                className={cx(
                  "flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2",
                  isSelected ? "border-primary-600" : "border-alpha-200"
                )}
              >
                <Show if={isSelected}>
                  <span className="block h-2 w-2 rounded-full bg-primary-600" />
                </Show>
              </span>
              <span className="text-sm font-bold text-content-1000">
                {option.title}
              </span>
            </span>

            <span className="inline-flex self-start rounded-full bg-primary-20 px-2.5 py-0.5 text-xs font-bold text-primary-600">
              {option.price}
            </span>

            <span className="text-xs leading-relaxed text-content-400">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>

    <Banner
      variant="primary"
      HugeIcon={
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={20}
          className="text-primary-600"
        />
      }
      title="Billing happens directly with Uloop"
      body="Boom doesn't process payment for this integration — you'll be billed by Uloop according to the model you choose above."
    />
  </div>
);
